import type {
	CanvasImageAsset,
	CanvasImageTransferBlock,
	CanvasImageTransferData,
	CanvasImageTransferDataParams,
	Rect,
	RenderRevealOptions,
	RenderTransferOptions
} from '@/types/canvas-image-effect';

const MAX_PIXEL_FRACTION = 0.2;
const BLOCK_SIZE = 6;
const DELAY_SPREAD = 0.55;
const REVEAL_TILE_SIZE = 14;
const REVEAL_PIXEL_RELEASE_START = 0.02;
const REVEAL_SCAN_START = 0.84;
const REVEAL_SCAN_SOFTNESS = 0.12;

const clamp = (value: number, min = 0, max = 1) => {
	return Math.min(max, Math.max(min, value));
};

const smoothstep = (edge0: number, edge1: number, value: number) => {
	const t = clamp((value - edge0) / (edge1 - edge0 || 1));
	return t * t * (3 - 2 * t);
};

const easeInOutCubic = (value: number) => {
	if (value < 0.5) {
		return 4 * value * value * value;
	}

	return 1 - (-2 * value + 2) ** 3 / 2;
};

const fract = (value: number) => {
	return value - Math.floor(value);
};

const hash2D = (x: number, y: number) => {
	return fract(Math.sin(x * 127.1 + y * 311.7) * 43758.5453123);
};

const getTargetRect = ({
	centerX,
	centerY,
	imageHeight,
	imageWidth
}: {
	centerX: number;
	centerY: number;
	imageHeight: number;
	imageWidth: number;
}): Rect => ({
	height: imageHeight,
	width: imageWidth,
	x: centerX - imageWidth / 2,
	y: centerY - imageHeight / 2
});

const drawAssetToRect = ({
	asset,
	context,
	targetRect
}: {
	asset: CanvasImageAsset;
	context: CanvasRenderingContext2D;
	targetRect: Rect;
}) => {
	context.drawImage(
		asset.image,
		asset.sourceRect.x,
		asset.sourceRect.y,
		asset.sourceRect.width,
		asset.sourceRect.height,
		targetRect.x,
		targetRect.y,
		targetRect.width,
		targetRect.height
	);
};

const getOpaqueBounds = (image: HTMLImageElement): Rect => {
	const canvas = document.createElement('canvas');
	canvas.width = image.naturalWidth;
	canvas.height = image.naturalHeight;

	const context = canvas.getContext('2d', { willReadFrequently: true });
	if (!context) {
		return {
			height: image.naturalHeight,
			width: image.naturalWidth,
			x: 0,
			y: 0
		};
	}

	context.clearRect(0, 0, canvas.width, canvas.height);
	context.drawImage(image, 0, 0);

	const { data, width, height } = context.getImageData(
		0,
		0,
		canvas.width,
		canvas.height
	);
	let minX = width;
	let minY = height;
	let maxX = -1;
	let maxY = -1;

	for (let y = 0; y < height; y += 1) {
		for (let x = 0; x < width; x += 1) {
			const alpha = data[(y * width + x) * 4 + 3];

			if (alpha > 8) {
				minX = Math.min(minX, x);
				minY = Math.min(minY, y);
				maxX = Math.max(maxX, x);
				maxY = Math.max(maxY, y);
			}
		}
	}

	if (maxX < minX || maxY < minY) {
		return {
			height: image.naturalHeight,
			width: image.naturalWidth,
			x: 0,
			y: 0
		};
	}

	return {
		height: maxY - minY + 1,
		width: maxX - minX + 1,
		x: minX,
		y: minY
	};
};

const createCanvasImageAsset = (image: HTMLImageElement): CanvasImageAsset => ({
	detailCanvas: document.createElement('canvas'),
	image,
	offscreenCanvas: document.createElement('canvas'),
	revealCanvas: document.createElement('canvas'),
	sourceRect: getOpaqueBounds(image)
});

const drawCleanTileReveal = ({
	asset,
	context,
	revealProgress,
	imageHeight,
	imageWidth,
	targetRect
}: {
	asset: CanvasImageAsset;
	context: CanvasRenderingContext2D;
	revealProgress: number;
	imageHeight: number;
	imageWidth: number;
	targetRect: Rect;
}) => {
	const scanProgress = smoothstep(REVEAL_SCAN_START, 1, revealProgress);

	if (scanProgress <= 0) {
		return;
	}

	const revealContext = asset.revealCanvas.getContext('2d');
	if (!revealContext) {
		return;
	}

	asset.revealCanvas.width = Math.max(1, Math.round(imageWidth));
	asset.revealCanvas.height = Math.max(1, Math.round(imageHeight));
	revealContext.imageSmoothingEnabled = true;
	revealContext.clearRect(
		0,
		0,
		asset.revealCanvas.width,
		asset.revealCanvas.height
	);
	revealContext.drawImage(
		asset.image,
		asset.sourceRect.x,
		asset.sourceRect.y,
		asset.sourceRect.width,
		asset.sourceRect.height,
		0,
		0,
		asset.revealCanvas.width,
		asset.revealCanvas.height
	);

	const columnCount = Math.ceil(imageWidth / REVEAL_TILE_SIZE);
	const rowCount = Math.ceil(imageHeight / REVEAL_TILE_SIZE);

	context.save();
	context.imageSmoothingEnabled = true;

	for (let row = 0; row < rowCount; row += 1) {
		for (let column = 0; column < columnCount; column += 1) {
			const tileX = column * REVEAL_TILE_SIZE;
			const tileY = row * REVEAL_TILE_SIZE;
			const tileWidth = Math.min(REVEAL_TILE_SIZE, imageWidth - tileX);
			const tileHeight = Math.min(REVEAL_TILE_SIZE, imageHeight - tileY);
			const normalizedX = columnCount <= 1 ? 0 : column / (columnCount - 1);
			const normalizedY = rowCount <= 1 ? 0 : row / (rowCount - 1);
			const scanThreshold =
				normalizedX * 0.78 +
				normalizedY * 0.08 +
				(hash2D(column * 3.1, row * 2.7) - 0.5) * 0.14;
			const localReveal = smoothstep(
				scanThreshold - REVEAL_SCAN_SOFTNESS,
				scanThreshold + REVEAL_SCAN_SOFTNESS,
				scanProgress
			);

			if (localReveal <= 0) {
				continue;
			}

			const destinationX = targetRect.x + tileX;
			const destinationY = targetRect.y + tileY;

			context.globalAlpha = localReveal;
			context.drawImage(
				asset.revealCanvas,
				tileX,
				tileY,
				tileWidth,
				tileHeight,
				destinationX,
				destinationY,
				tileWidth,
				tileHeight
			);
		}
	}

	context.restore();
};

const blockHasVisiblePixel = ({
	blockHeight,
	blockWidth,
	blockX,
	blockY,
	imageData,
	renderedWidth
}: {
	blockHeight: number;
	blockWidth: number;
	blockX: number;
	blockY: number;
	imageData: Uint8ClampedArray;
	renderedWidth: number;
}) => {
	for (let y = 0; y < blockHeight; y += 1) {
		for (let x = 0; x < blockWidth; x += 1) {
			const alpha =
				imageData[((blockY + y) * renderedWidth + (blockX + x)) * 4 + 3];

			if (alpha > 20) {
				return true;
			}
		}
	}

	return false;
};

const createTransferBlock = ({
	blockX,
	blockY,
	columnCount,
	endLeft,
	startLeft,
	top,
	travelDistance
}: {
	blockX: number;
	blockY: number;
	columnCount: number;
	endLeft: number;
	startLeft: number;
	top: number;
	travelDistance: number;
}): CanvasImageTransferBlock => {
	const columnIndex = Math.floor(blockX / BLOCK_SIZE);
	const columnProgress = columnCount <= 1 ? 0 : columnIndex / (columnCount - 1);
	const jitter = (hash2D(blockX, blockY) - 0.5) * 0.08;
	const delay = clamp(columnProgress * DELAY_SPREAD + jitter, 0, 0.78);
	const duration = clamp(0.34 + hash2D(blockY, blockX) * 0.18, 0.3, 0.56);
	const arcFactor = 0.1 + hash2D(blockX * 3, blockY * 5) * 0.22;
	const rotationSpeed = (hash2D(blockX * 7, blockY * 11) - 0.5) * 1.8;
	const sign = hash2D(blockX * 13, blockY * 17) > 0.5 ? 1 : -1;

	return {
		arcHeight: travelDistance * arcFactor,
		blockSize: BLOCK_SIZE,
		delay,
		duration,
		rotationSpeed,
		scaleBoost: 0.08 + hash2D(blockX * 19, blockY * 23) * 0.08,
		sign,
		sourceX: startLeft + blockX,
		sourceY: top + blockY,
		targetX: endLeft + blockX,
		targetY: top + blockY
	};
};

const createRenderedCanvas = ({
	image,
	imageHeight,
	imageWidth,
	sourceRect
}: {
	image: HTMLImageElement;
	imageHeight: number;
	imageWidth: number;
	sourceRect: Rect;
}) => {
	const renderedCanvas = document.createElement('canvas');
	renderedCanvas.width = Math.max(1, Math.round(imageWidth));
	renderedCanvas.height = Math.max(1, Math.round(imageHeight));

	const renderedContext = renderedCanvas.getContext('2d', {
		willReadFrequently: true
	});
	if (!renderedContext) {
		return null;
	}

	renderedContext.clearRect(0, 0, renderedCanvas.width, renderedCanvas.height);
	renderedContext.drawImage(
		image,
		sourceRect.x,
		sourceRect.y,
		sourceRect.width,
		sourceRect.height,
		0,
		0,
		renderedCanvas.width,
		renderedCanvas.height
	);

	return {
		imageData: renderedContext.getImageData(
			0,
			0,
			renderedCanvas.width,
			renderedCanvas.height
		).data,
		renderedCanvas
	};
};

const createCanvasImageTransferData = ({
	centerY,
	endCenterX,
	image,
	imageHeight,
	imageWidth,
	sourceRect,
	startCenterX
}: CanvasImageTransferDataParams): CanvasImageTransferData | null => {
	const renderedData = createRenderedCanvas({
		image,
		imageHeight,
		imageWidth,
		sourceRect
	});
	if (!renderedData) {
		return null;
	}

	const { imageData, renderedCanvas } = renderedData;
	const startLeft = startCenterX - imageWidth / 2;
	const endLeft = endCenterX - imageWidth / 2;
	const top = centerY - imageHeight / 2;
	const travelDistance = Math.abs(endLeft - startLeft);
	const columnCount = Math.max(1, Math.ceil(renderedCanvas.width / BLOCK_SIZE));
	const blocks: CanvasImageTransferBlock[] = [];

	for (let blockY = 0; blockY < renderedCanvas.height; blockY += BLOCK_SIZE) {
		for (let blockX = 0; blockX < renderedCanvas.width; blockX += BLOCK_SIZE) {
			const blockWidth = Math.min(BLOCK_SIZE, renderedCanvas.width - blockX);
			const blockHeight = Math.min(BLOCK_SIZE, renderedCanvas.height - blockY);

			if (
				!blockHasVisiblePixel({
					blockHeight,
					blockWidth,
					blockX,
					blockY,
					imageData,
					renderedWidth: renderedCanvas.width
				})
			) {
				continue;
			}

			blocks.push(
				createTransferBlock({
					blockX,
					blockY,
					columnCount,
					endLeft,
					startLeft,
					top,
					travelDistance
				})
			);
		}
	}

	return {
		blocks,
		renderedCanvas
	};
};

const createMainContextAccessor = (canvas: HTMLCanvasElement) => {
	let lastDpr = 0;
	let lastHeight = 0;
	let lastWidth = 0;

	return ({ height, width }: { height: number; width: number }) => {
		const context = canvas.getContext('2d');
		if (!context) {
			return null;
		}

		const dpr = window.devicePixelRatio || 1;
		const nextWidth = Math.max(1, Math.round(width * dpr));
		const nextHeight = Math.max(1, Math.round(height * dpr));
		if (
			lastDpr !== dpr ||
			lastWidth !== nextWidth ||
			lastHeight !== nextHeight
		) {
			canvas.width = nextWidth;
			canvas.height = nextHeight;
			canvas.style.width = `${width}px`;
			canvas.style.height = `${height}px`;
			lastDpr = dpr;
			lastWidth = nextWidth;
			lastHeight = nextHeight;
		}
		context.setTransform(dpr, 0, 0, dpr, 0, 0);
		context.clearRect(0, 0, width, height);

		return context;
	};
};

const getSourceOffset = ({
	block,
	centerY,
	imageHeight,
	imageWidth,
	startCenterX
}: {
	block: CanvasImageTransferBlock;
	centerY: number;
	imageHeight: number;
	imageWidth: number;
	startCenterX: number;
}) => ({
	sourceOffsetX: block.sourceX - (startCenterX - imageWidth / 2),
	sourceOffsetY: block.sourceY - (centerY - imageHeight / 2)
});

const drawSettledBlock = ({
	block,
	centerY,
	context,
	destinationX,
	destinationY,
	imageHeight,
	imageWidth,
	renderedImageCanvas,
	startCenterX
}: {
	block: CanvasImageTransferBlock;
	centerY: number;
	context: CanvasRenderingContext2D;
	destinationX: number;
	destinationY: number;
	imageHeight: number;
	imageWidth: number;
	renderedImageCanvas: HTMLCanvasElement;
	startCenterX: number;
}) => {
	const { sourceOffsetX, sourceOffsetY } = getSourceOffset({
		block,
		centerY,
		imageHeight,
		imageWidth,
		startCenterX
	});

	context.drawImage(
		renderedImageCanvas,
		sourceOffsetX,
		sourceOffsetY,
		block.blockSize,
		block.blockSize,
		destinationX,
		destinationY,
		block.blockSize,
		block.blockSize
	);
};

const drawFlyingBlock = ({
	block,
	centerY,
	context,
	eased,
	imageHeight,
	imageWidth,
	renderedImageCanvas,
	startCenterX
}: {
	block: CanvasImageTransferBlock;
	centerY: number;
	context: CanvasRenderingContext2D;
	eased: number;
	imageHeight: number;
	imageWidth: number;
	renderedImageCanvas: HTMLCanvasElement;
	startCenterX: number;
}) => {
	const x = block.sourceX + (block.targetX - block.sourceX) * eased;
	const y =
		block.sourceY +
		(block.targetY - block.sourceY) * eased -
		Math.sin(Math.PI * eased) * block.arcHeight * block.sign;
	const scale = 1 + Math.sin(Math.PI * eased) * block.scaleBoost;
	const rotation = Math.sin(Math.PI * eased) * block.rotationSpeed;
	const { sourceOffsetX, sourceOffsetY } = getSourceOffset({
		block,
		centerY,
		imageHeight,
		imageWidth,
		startCenterX
	});

	context.save();
	context.translate(x + block.blockSize / 2, y + block.blockSize / 2);
	context.rotate(rotation);
	context.scale(scale, scale);
	context.drawImage(
		renderedImageCanvas,
		sourceOffsetX,
		sourceOffsetY,
		block.blockSize,
		block.blockSize,
		-block.blockSize / 2,
		-block.blockSize / 2,
		block.blockSize,
		block.blockSize
	);
	context.restore();
};

const createCanvasImageController = (canvas: HTMLCanvasElement) => {
	const getMainContext = createMainContextAccessor(canvas);

	return {
		revealImage({
			asset,
			centerX,
			centerY,
			imageHeight,
			imageWidth,
			progress,
			stageHeight,
			stageWidth
		}: RenderRevealOptions) {
			const context = getMainContext({
				height: stageHeight,
				width: stageWidth
			});
			if (!context) {
				return;
			}

			const targetRect = getTargetRect({
				centerX,
				centerY,
				imageHeight,
				imageWidth
			});
			const revealProgress = clamp(progress);
			const heldProgress = smoothstep(
				REVEAL_PIXEL_RELEASE_START,
				1,
				revealProgress
			);
			const maxPixelSize = Math.max(
				2,
				Math.round(Math.max(stageWidth, stageHeight) * MAX_PIXEL_FRACTION)
			);
			const continuousPixelSize = Math.max(
				1,
				maxPixelSize * (1 - heldProgress)
			);
			const coarsePixelSize = Math.max(1, Math.ceil(continuousPixelSize));
			const finePixelSize = Math.max(1, Math.floor(continuousPixelSize));
			const pixelBlendProgress =
				coarsePixelSize === finePixelSize
					? 0
					: clamp(
							(coarsePixelSize - continuousPixelSize) /
								(coarsePixelSize - finePixelSize)
						);

			if (continuousPixelSize <= 1.05) {
				context.imageSmoothingEnabled = true;
				drawAssetToRect({ asset, context, targetRect });
				return;
			}

			const offscreenContext = asset.offscreenCanvas.getContext('2d');
			const detailContext = asset.detailCanvas.getContext('2d');

			if (!offscreenContext || !detailContext) {
				return;
			}

			const drawPixelLayer = (
				layerCanvas: HTMLCanvasElement,
				layerContext: CanvasRenderingContext2D,
				pixelSize: number
			) => {
				const sampledWidth = Math.max(1, Math.ceil(imageWidth / pixelSize));
				const sampledHeight = Math.max(1, Math.ceil(imageHeight / pixelSize));

				layerCanvas.width = sampledWidth;
				layerCanvas.height = sampledHeight;
				layerContext.imageSmoothingEnabled = false;
				layerContext.clearRect(0, 0, sampledWidth, sampledHeight);
				layerContext.drawImage(
					asset.image,
					asset.sourceRect.x,
					asset.sourceRect.y,
					asset.sourceRect.width,
					asset.sourceRect.height,
					0,
					0,
					sampledWidth,
					sampledHeight
				);

				return { sampledHeight, sampledWidth };
			};

			const coarseLayer = drawPixelLayer(
				asset.offscreenCanvas,
				offscreenContext,
				coarsePixelSize
			);

			context.imageSmoothingEnabled = false;
			context.globalAlpha = 1;
			context.drawImage(
				asset.offscreenCanvas,
				0,
				0,
				coarseLayer.sampledWidth,
				coarseLayer.sampledHeight,
				targetRect.x,
				targetRect.y,
				targetRect.width,
				targetRect.height
			);

			if (finePixelSize < coarsePixelSize) {
				const fineLayer = drawPixelLayer(
					asset.detailCanvas,
					detailContext,
					finePixelSize
				);

				context.globalAlpha = pixelBlendProgress;
				context.drawImage(
					asset.detailCanvas,
					0,
					0,
					fineLayer.sampledWidth,
					fineLayer.sampledHeight,
					targetRect.x,
					targetRect.y,
					targetRect.width,
					targetRect.height
				);
			}

			drawCleanTileReveal({
				asset,
				context,
				revealProgress,
				imageHeight,
				imageWidth,
				targetRect
			});

			context.globalAlpha = 1;
		},

		transferImageToRight({
			centerY,
			imageHeight,
			imageWidth,
			progress,
			settledImageOpacity = 0,
			stageHeight,
			stageWidth,
			startCenterX,
			transferData
		}: RenderTransferOptions) {
			const context = getMainContext({
				height: stageHeight,
				width: stageWidth
			});
			if (!context) {
				return;
			}

			context.imageSmoothingEnabled = true;

			for (const block of transferData.blocks) {
				const localTime = progress - block.delay;

				if (localTime <= 0) {
					drawSettledBlock({
						block,
						centerY,
						context,
						destinationX: block.sourceX,
						destinationY: block.sourceY,
						imageHeight,
						imageWidth,
						renderedImageCanvas: transferData.renderedCanvas,
						startCenterX
					});
					continue;
				}

				const normalizedProgress = clamp(localTime / block.duration);
				const eased = easeInOutCubic(normalizedProgress);

				if (normalizedProgress >= 1) {
					drawSettledBlock({
						block,
						centerY,
						context,
						destinationX: block.targetX,
						destinationY: block.targetY,
						imageHeight,
						imageWidth,
						renderedImageCanvas: transferData.renderedCanvas,
						startCenterX
					});
					continue;
				}

				drawFlyingBlock({
					block,
					centerY,
					context,
					eased,
					imageHeight,
					imageWidth,
					renderedImageCanvas: transferData.renderedCanvas,
					startCenterX
				});
			}

			if (settledImageOpacity > 0) {
				const finalLeft = transferData.blocks[0]
					? transferData.blocks[0].targetX -
						(transferData.blocks[0].sourceX - (startCenterX - imageWidth / 2))
					: 0;
				context.save();
				context.globalAlpha = clamp(settledImageOpacity);
				context.drawImage(
					transferData.renderedCanvas,
					finalLeft,
					centerY - imageHeight / 2,
					imageWidth,
					imageHeight
				);
				context.restore();
			}
		}
	};
};

export {
	createCanvasImageAsset,
	createCanvasImageController,
	createCanvasImageTransferData,
	getOpaqueBounds
};
